import api from './api';

export interface SignalCampaignVariant {
  id: string;
  campaignId: string;
  label: string;
  angle?: string | null;
  assignedCount: number;
  sentCount: number;
  openedCount: number;
  repliedCount: number;
  positiveCount: number;
}

export interface SignalCampaignContact {
  id: string;
  campaignId: string;
  variantId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  company?: string | null;
  jobTitle?: string | null;
  linkedInUrl?: string | null;
  linkedInUrn?: string | null;
  signalText?: string | null;
  signalUrl?: string | null;
  signalPostedAt?: string | null;
  signalFetchedAt?: string | null;
  signalStatus: 'pending' | 'fetched' | 'no_post' | 'error';
  signalError?: string | null;
  message?: string | null;
  messageGeneratedAt?: string | null;
  lemlistLeadId?: string | null;
  lemlistSyncedAt?: string | null;
  status: 'new' | 'signal_ready' | 'message_ready' | 'sent' | 'opened' | 'replied' | 'bounced';
  outcome?: string | null;
  openedAt?: string | null;
  repliedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SignalCampaign {
  id: string;
  name: string;
  description?: string | null;
  status: 'draft' | 'collecting' | 'ready' | 'active' | 'completed';
  goal?: string | null;
  tone: string;
  language: string;
  variantCount: number;
  lemlistCampaignId?: string | null;
  icpId?: string | null;
  personaId?: string | null;
  offerId?: string | null;
  userId: string;
  createdAt: string;
  updatedAt: string;
  variants?: SignalCampaignVariant[];
  contacts?: SignalCampaignContact[];
  _count?: { contacts: number };
}

export interface CreateSignalCampaignInput {
  name: string;
  description?: string;
  goal?: string;
  tone?: string;
  language?: string;
  variantCount?: number;
  lemlistCampaignId?: string;
  variantAngles?: string[];
  icpId?: string;
  personaId?: string;
  offerId?: string;
}

export interface AddSignalContactInput {
  firstName?: string;
  lastName?: string;
  email?: string;
  company?: string;
  jobTitle?: string;
  linkedInUrl?: string;
  linkedInUrn?: string;
  signalText?: string;
}

export interface VariantResult extends SignalCampaignVariant {
  openRate: number;
  replyRate: number;
  positiveRate: number;
}

export interface AbResults {
  variants: VariantResult[];
  totalSent: number;
  winnerId: string | null;
  winnerLabel: string | null;
  conclusive: boolean;
}

export interface LemlistCampaignOption {
  _id: string;
  name: string;
}

class SignalCampaignService {
  async getCampaigns(status?: string): Promise<SignalCampaign[]> {
    const response = await api.get('/signal-campaigns/campaigns', {
      params: status ? { status } : {},
    });
    return response.data;
  }

  async getCampaign(id: string): Promise<SignalCampaign> {
    const response = await api.get(`/signal-campaigns/campaigns/${id}`);
    return response.data;
  }

  async createCampaign(input: CreateSignalCampaignInput): Promise<SignalCampaign> {
    const response = await api.post('/signal-campaigns/campaigns', input);
    return response.data;
  }

  async updateCampaign(id: string, input: Partial<CreateSignalCampaignInput> & { status?: string }): Promise<SignalCampaign> {
    const response = await api.put(`/signal-campaigns/campaigns/${id}`, input);
    return response.data;
  }

  async deleteCampaign(id: string): Promise<void> {
    await api.delete(`/signal-campaigns/campaigns/${id}`);
  }

  async addContacts(campaignId: string, contacts: AddSignalContactInput[]): Promise<SignalCampaignContact[]> {
    const response = await api.post(`/signal-campaigns/campaigns/${campaignId}/contacts`, { contacts });
    return response.data;
  }

  async updateContact(id: string, input: Partial<AddSignalContactInput> & { message?: string; outcome?: string }): Promise<SignalCampaignContact> {
    const response = await api.put(`/signal-campaigns/contacts/${id}`, input);
    return response.data;
  }

  async deleteContact(id: string): Promise<void> {
    await api.delete(`/signal-campaigns/contacts/${id}`);
  }

  async fetchSignals(campaignId: string): Promise<{ fetched: number; noPost: number; errors: number; processed: number }> {
    const response = await api.post(`/signal-campaigns/campaigns/${campaignId}/fetch-signals`);
    return response.data;
  }

  async generateMessages(campaignId: string): Promise<{ generated: number; failed: number; processed: number }> {
    const response = await api.post(`/signal-campaigns/campaigns/${campaignId}/generate-messages`);
    return response.data;
  }

  async syncToLemlist(campaignId: string): Promise<{ synced: number; failed: number; details: any[] }> {
    const response = await api.post(`/signal-campaigns/campaigns/${campaignId}/sync-lemlist`);
    return response.data;
  }

  async trackOutcomes(campaignId: string): Promise<{ updated: number; processed: number }> {
    const response = await api.post(`/signal-campaigns/campaigns/${campaignId}/track-outcomes`);
    return response.data;
  }

  async getAbResults(campaignId: string): Promise<AbResults> {
    const response = await api.get(`/signal-campaigns/campaigns/${campaignId}/ab-results`);
    return response.data;
  }

  async getLemlistCampaigns(): Promise<LemlistCampaignOption[]> {
    const response = await api.get('/lemlist/campaigns');
    return response.data;
  }
}

const signalCampaignService = new SignalCampaignService();
export default signalCampaignService;
